import React, { useState } from 'react';

import { defineMessage, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	cardWrapper: {
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.xlarge'),
	},
	cardHeader: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	cardHeaderExpanded: {
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	},
	cardContent: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	headerIconWrapper: {
		flexShrink: 0,
		alignSelf: 'center',
	},
});

const collapseMessage = defineMessage({
	id: 'agentic-planning.collapsible-card.collapse',
	defaultMessage: 'Collapse',
	description: 'Label for the collapse button in the collapsible card',
});

const expandMessage = defineMessage({
	id: 'agentic-planning.collapsible-card.expand',
	defaultMessage: 'Expand',
	description: 'Label for the expand button in the collapsible card',
});

export const CollapsibleCard = ({
	title,
	subtitle,
	children,
	isExpandable = false,
	defaultExpanded = true,
	headerIcon,
}: {
	children: React.ReactNode;
	defaultExpanded?: boolean;
	headerIcon?: React.ReactNode;
	isExpandable?: boolean;
	subtitle?: React.ReactNode;
	title?: string;
}): JSX.Element => {
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const { formatMessage } = useIntl();

	const shouldShowContent = !isExpandable || isExpanded;
	const hasHeader = Boolean(title || headerIcon);

	return (
		<Box xcss={styles.cardWrapper} testId="collapsible-card">
			{hasHeader && (
				<Box
					xcss={cx(styles.cardHeader, shouldShowContent ? styles.cardHeaderExpanded : undefined)}
				>
					<Inline space="space.100" alignBlock="center" spread="space-between">
						<Inline space="space.100" alignBlock="center">
							<Box xcss={styles.headerIconWrapper}>{headerIcon}</Box>
							<Stack>
								{title && (
									<Text size="large" weight="bold">
										{title}
									</Text>
								)}
								{React.isValidElement(subtitle) ? (
									subtitle
								) : (
									<Text size="small" color="color.text.subtlest">
										{subtitle}
									</Text>
								)}
							</Stack>
						</Inline>
						{isExpandable && (
							<IconButton
								appearance="subtle"
								icon={isExpanded ? ChevronUpIcon : ChevronDownIcon}
								label={isExpanded ? formatMessage(collapseMessage) : formatMessage(expandMessage)}
								onClick={() => setIsExpanded((prev) => !prev)}
							/>
						)}
					</Inline>
				</Box>
			)}
			{shouldShowContent && <Box xcss={styles.cardContent}>{children}</Box>}
		</Box>
	);
};
